import React, { useState } from "react";
import { Input, Modal, notification } from "antd";
import { Loader2, Sparkles } from "lucide-react";
import WebUtils from "../../../../../utils/WebUtils";
import { generateAiResumeContentApi } from "../../../../../api/b2b-candidate";
import Utils from "../../../../../utils/Utils";

const { TextArea } = Input;

function WriteWithAiSummaryModal({
  open,
  setOpen,
  currentSummary,
  experiences,
  projects,
  onApply,
}) {
  const userDetail = Utils.getUserDetail();

  const [instructions, setInstructions] = useState("");
  const [aiSummary, setAiSummary] = useState("");
  const [generating, setGenerating] = useState(false);

  const hasGenerated = !!aiSummary;

  const handleClose = () => {
    setInstructions("");
    setAiSummary("");
    setGenerating(false);
    setOpen(false);
  };

  const handleGenerate = async () => {
    let req = {};
    if (userDetail?.userRole === "JOBDRONA_CANDIDATE") {
      req = {
        candidateContext: {
          experiences: experiences ?? [],
          projects: projects ?? [],
        },
        currentSectionKey: "professionalSummary",
        currentSectionContent: hasGenerated ? aiSummary : currentSummary,
        language: "English",
        instructions: instructions?.trim() ?? "",
        userIdentifier: userDetail?.userIdentifier,
      };
    } else {
      req = {
        organizationId: userDetail?.organizationId,
        candidateContext: {
          experiences: experiences ?? [],
          projects: projects ?? [],
        },
        currentSectionKey: "professionalSummary",
        currentSectionContent: hasGenerated ? aiSummary : currentSummary,
        language: "English",
        instructions: instructions?.trim() ?? "",
        userIdentifier: userDetail?.userIdentifier,
      };
    }
    try {
      setGenerating(true);
      const result = await WebUtils.httpOperations(
        generateAiResumeContentApi,
        req,
        "POST",
      );

      if (
        result?.data?.resultStatusInfo?.resultCode?.toLowerCase() === "success"
      ) {
        setAiSummary(result?.data?.data?.professionalSummary ?? "");
        notification.success({
          message: "Success",
          description: "Summary generated!",
          placement: "topRight",
        });
      } else {
        notification.error({
          message: "Failed",
          description: result?.data?.resultStatusInfo?.message,
          placement: "topRight",
        });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setGenerating(false);
    }
  };

  const handleApply = () => {
    onApply(aiSummary);
    handleClose();
  };

  return (
    <Modal
      open={open}
      onCancel={handleClose}
      footer={null}
      centered
      width={950}
    >
      <div className="flex items-center gap-3 mb-1">
        <div className="border border-[#E4E7EC] px-[.65rem] py-[.65rem] rounded-lg shadow">
          <Sparkles size={19} />
        </div>
        <div>
          <p className="text-[1.05rem] font-semibold text-[#0F1B23] mb-0">
            Tell AI what to improve
          </p>
          <p className="text-sm text-[#475467]">
            Describe the improvements you're looking for and let AI optimize
            accordingly.{" "}
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-2 gap-5">
        <div className="flex flex-col gap-1">
          <p className="text-[.85rem] font-medium text-[#344054] mb-1">
            Existing current summary
          </p>
          <TextArea
            value={currentSummary ?? ""}
            readOnly
            rows={10}
            className="bg-[#F9FAFB] text-[.85rem]"
          />
        </div>

        <div className="flex flex-col gap-1">
          <p className="text-[.85rem] font-medium text-[#344054] mb-1">
            AI Generated Summary
          </p>
          <TextArea
            value={aiSummary}
            onChange={(e) => setAiSummary(e.target.value)}
            placeholder={
              generating
                ? "Generating..."
                : "AI generated summary will appear here"
            }
            rows={10}
            className="text-[.85rem]"
          />
        </div>
      </div>

      <div className="mt-5">
        <p className="text-[.85rem] font-medium text-[#344054] mb-1">
          Describe changes you would like{" "}
        </p>
        <TextArea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          placeholder="Highlight leadership, ownership, and measurable achievements."
          autoSize={{ minRows: 2, maxRows: 4 }}
        />
      </div>
      {userDetail?.userRole === "JOBDRONA_CANDIDATE" && (
        <div className="mt-3 text-red-500">
          <strong>Note</strong> :{" "}
          <span>
            <strong>1</strong> credits will be detected for every AI generation
          </span>
        </div>
      )}

      <div className="flex justify-end items-center gap-3 mt-6">
        {/* <button
          className="btn-white px-3 py-2 rounded-lg sm:w-1/2 w-full"
          onClick={handleClose}
        >
          Cancel
        </button> */}

        {!hasGenerated ? (
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="btn-orange px-16 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60"
          >
            {generating ? (
              <Loader2 size={16} className="animate-spin" />
            ) : (
              <Sparkles size={16} />
            )}
            {generating ? "Generating..." : "Generate with AI"}
          </button>
        ) : (
          <>
            <button
              onClick={handleGenerate}
              disabled={generating}
              className="btn-white px-16 py-2 rounded-lg flex items-center justify-center gap-2 disabled:opacity-60"
            >
              {generating ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Sparkles size={16} />
              )}
              {generating ? "Regenerating..." : "Regenerate"}
            </button>
            <button
              onClick={handleApply}
              disabled={generating || !aiSummary?.trim()}
              className="btn-orange px-16 py-2 rounded-lg disabled:opacity-60"
            >
              Apply Changes
            </button>
          </>
        )}
      </div>
    </Modal>
  );
}

export default WriteWithAiSummaryModal;
